import React, { useState, useEffect } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import { getActivityById, getEquivalentActivities, deleteActivity } from '../api/activities';
import { formatActivityName, fetchDefaultAvatarSvg } from '../utils';
import { getAvatar } from '../api/avatars';
import { getReactions, getAllowedEmojis, toggleReaction } from '../api/reactions';
import { getUserId } from '../storage';
import Avatar from '../components/Avatar';
import '../styles/ActivityDetails.css';

const DEFAULT_REACTION_EMOJIS = ['👍', '❤️', '🔥', '💪', '🎉', '👏'];

const formatDate = (dateString) => {
    if (!dateString) return '';
    return new Date(`${dateString}T00:00`).toLocaleDateString('en-US', {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric',
    });
};

const formatLoggedAt = (iso) => {
    if (!iso) return '';
    const date = new Date(iso);
    if (Number.isNaN(date.getTime())) return '';
    return date.toLocaleString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
    });
};

const formatTime = (minutes) => {
    const total = Math.max(0, Math.round(Number(minutes) || 0));
    const hours = Math.floor(total / 60);
    const mins = total % 60;
    if (hours > 0 && mins > 0) return `${hours}h ${mins}m`;
    if (hours > 0) return `${hours}h`;
    return `${mins}m`;
};

const formatPoints = (points) => {
    const value = Number(points);
    if (!Number.isFinite(value)) return '0';
    return String(Math.round(value * 100) / 100);
};

const formatBonus = (bonus) => {
    const pct = Math.round((Number(bonus) || 0) * 100);
    return `${pct > 0 ? '+' : ''}${pct}%`;
};

const SummaryStat = ({ label, value, tone, meter }) => (
    <div className={`ad-stat${tone ? ` ad-stat--${tone}` : ''}`}>
        <span className="ad-stat-value">{value}</span>
        <span className={`ad-stat-meter${meter != null ? ' is-active' : ''}`} aria-hidden="true">
            {meter != null && (
                <span className="ad-stat-meter-fill" style={{ width: `${meter}%` }} />
            )}
        </span>
        <span className="ad-stat-label">{label}</span>
    </div>
);

const ActivityDetailsPage = () => {
    const { activityId } = useParams();
    const history = useHistory();
    const [activity, setActivity] = useState(null);
    const [userAvatar, setUserAvatar] = useState('');
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [actionError, setActionError] = useState(null);
    const [activityReactions, setActivityReactions] = useState([]);
    const [currentUserId, setCurrentUserId] = useState(null);
    const [allowedEmojis, setAllowedEmojis] = useState([]);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const [equivalentActivities, setEquivalentActivities] = useState([]);
    const [equivalentActivitiesError, setEquivalentActivitiesError] = useState(null);
    const [equivalentActivitiesLoading, setEquivalentActivitiesLoading] = useState(false);

    useEffect(() => {
        setCurrentUserId(getUserId());
    }, []);

    useEffect(() => {
        const fetchActivityDetails = async () => {
            setLoading(true);
            setError(null);
            setActionError(null);
            setEquivalentActivities([]);
            setEquivalentActivitiesError(null);
            try {
                const data = await getActivityById(activityId);
                setActivity(data);

                if (data?.points && data?.activity) {
                    setEquivalentActivitiesLoading(true);
                    try {
                        const conversionData = await getEquivalentActivities(data.points, data.activity);
                        setEquivalentActivities(
                            Array.isArray(conversionData.conversions) ? conversionData.conversions : []
                        );
                    } catch (conversionErr) {
                        setEquivalentActivitiesError(
                            conversionErr.message || 'Failed to load equivalent activities'
                        );
                    } finally {
                        setEquivalentActivitiesLoading(false);
                    }
                }

                try {
                    const emojis = await getAllowedEmojis();
                    setAllowedEmojis(emojis);
                } catch (e) {
                    console.error('Failed to fetch allowed emojis:', e);
                }

                try {
                    const reactionsData = await getReactions(activityId);
                    setActivityReactions(reactionsData.reactions || []);
                } catch (e) {
                    console.error('Failed to fetch reactions:', e);
                }

                if (data.avatar_id) {
                    try {
                        const avatarData = await getAvatar(data.avatar_id);
                        setUserAvatar(avatarData.avatar.svg_data);
                    } catch (avatarErr) {
                        console.error('Failed to load user avatar:', avatarErr);
                        const defaultSvg = await fetchDefaultAvatarSvg();
                        setUserAvatar(defaultSvg);
                    }
                } else {
                    const defaultSvg = await fetchDefaultAvatarSvg();
                    setUserAvatar(defaultSvg);
                }
            } catch (err) {
                setError(err.message || 'Failed to fetch activity details');
            } finally {
                setLoading(false);
            }
        };

        fetchActivityDetails();
    }, [activityId]);

    const handleDelete = async () => {
        setDeleting(true);
        setActionError(null);
        try {
            await deleteActivity(activityId);
            history.goBack();
        } catch (err) {
            setActionError(err.message || 'Failed to delete activity');
            setDeleting(false);
            setShowDeleteConfirm(false);
        }
    };

    const handleToggleReaction = async (emoji) => {
        setActionError(null);
        try {
            await toggleReaction(activityId, emoji);
            const data = await getReactions(activityId);
            setActivityReactions(data.reactions || []);
        } catch (e) {
            setActionError(e.message || 'Failed to update reaction');
        }
    };

    if (loading) {
        return (
            <div className="sc-page">
                <div className="sc-container">
                    <div className="sc-loading">
                        <div className="sc-spinner"></div>
                        <p>Loading activity details...</p>
                    </div>
                </div>
            </div>
        );
    }

    if (error || !activity) {
        return (
            <div className="sc-page">
                <div className="sc-container">
                    <div className="ad-back-bar">
                        <button type="button" className="sc-back-link" onClick={() => history.goBack()}>
                            &larr; Back
                        </button>
                    </div>
                    {error ? (
                        <div className="ad-state">
                            <div className="sc-alert sc-alert--error">{error}</div>
                        </div>
                    ) : (
                        <p className="ad-empty">Activity not found.</p>
                    )}
                </div>
            </div>
        );
    }

    const isOwnActivity = activity.user_id === currentUserId;
    const activityTitle = activity.activity_name || formatActivityName(activity.activity);
    const bonusPct = Math.round((Number(activity.effort_bonus) || 0) * 100);
    const effortPct = Math.min(100, Math.max(0, (Number(activity.personal_effort) || 0) * 10));
    const hasAnswers = Boolean(activity.primary_answer_code || activity.secondary_answer_code);
    const showEquivalents = equivalentActivitiesLoading
        || Boolean(equivalentActivitiesError)
        || equivalentActivities.length > 0;
    const emojiChoices = allowedEmojis.length > 0 ? allowedEmojis : DEFAULT_REACTION_EMOJIS;
    const loggedAt = formatLoggedAt(activity.created_at);

    const reactionGroups = Object.values(
        activityReactions.reduce((groups, reaction) => {
            if (!groups[reaction.emoji]) {
                groups[reaction.emoji] = { emoji: reaction.emoji, users: [] };
            }
            groups[reaction.emoji].users.push(reaction);
            return groups;
        }, {})
    );

    const openProfile = () => {
        if (activity.username) history.push(`/user/${activity.username}`);
    };

    return (
        <div className="sc-page">
            <div className="sc-container">
                {showDeleteConfirm && (
                    <div className="sc-overlay ad-overlay">
                        <div
                            className="sc-modal ad-modal"
                            role="dialog"
                            aria-modal="true"
                            aria-labelledby="ad-delete-title"
                        >
                            <h2 id="ad-delete-title">Delete activity?</h2>
                            <p>
                                This will permanently remove this activity and deduct{' '}
                                <strong>{formatPoints(activity.points)} SP</strong> from your total.
                                This action cannot be undone.
                            </p>
                            <div className="ad-modal-actions">
                                <button
                                    type="button"
                                    className="sc-btn sc-btn--secondary"
                                    onClick={() => setShowDeleteConfirm(false)}
                                    disabled={deleting}
                                >
                                    Cancel
                                </button>
                                <button
                                    type="button"
                                    className="sc-btn sc-btn--danger"
                                    onClick={handleDelete}
                                    disabled={deleting}
                                >
                                    {deleting ? 'Deleting...' : 'Delete activity'}
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                <div className="ad-back-bar">
                    <button type="button" className="sc-back-link" onClick={() => history.goBack()}>
                        &larr; Back
                    </button>
                </div>

                <header className="sc-header ad-header">
                    <div className="ad-header-text">
                        <h2>{activityTitle}</h2>
                        <p className="ad-header-sub">{formatDate(activity.date)}</p>
                    </div>
                </header>

                <section className="ad-stats" aria-label="Summary">
                    <SummaryStat label="SP" value={formatPoints(activity.points)} tone="points" />
                    <SummaryStat label="Time" value={formatTime(activity.time)} />
                    <SummaryStat
                        label="Effort"
                        tone="effort"
                        meter={effortPct}
                        value={(
                            <>
                                {activity.personal_effort ?? '—'}
                                {activity.personal_effort != null && (
                                    <span className="ad-stat-unit">/10</span>
                                )}
                            </>
                        )}
                    />
                    <SummaryStat
                        label="Effort Bonus"
                        value={formatBonus(activity.effort_bonus)}
                        tone={bonusPct > 0 ? 'bonus' : undefined}
                    />
                </section>

                <div className="ad-content">
                    {actionError && (
                        <div className="sc-alert sc-alert--error">{actionError}</div>
                    )}

                    {hasAnswers && (
                        <section className="ad-section">
                            <h3 className="ad-section-title">Details</h3>
                            <dl className="ad-details">
                                {activity.primary_answer_code && (
                                    <>
                                        <dt>{activity.primary_question_text || 'Answer'}</dt>
                                        <dd>{activity.primary_answer_text || activity.primary_answer_code}</dd>
                                    </>
                                )}
                                {activity.secondary_answer_code && (
                                    <>
                                        <dt>{activity.secondary_question_text || 'Answer'}</dt>
                                        <dd>{activity.secondary_answer_text || activity.secondary_answer_code}</dd>
                                    </>
                                )}
                            </dl>
                        </section>
                    )}

                    {showEquivalents && (
                        <section className="ad-section">
                            <div className="ad-section-head">
                                <h3 className="ad-section-title">Same effort</h3>
                                <p>
                                    {formatTime(activity.time)} of {activityTitle} is also about...
                                </p>
                            </div>
                            {equivalentActivitiesLoading && (
                                <p className="ad-note">Calculating equivalent activities...</p>
                            )}
                            {!equivalentActivitiesLoading && equivalentActivitiesError && (
                                <p className="ad-note ad-note--error">{equivalentActivitiesError}</p>
                            )}
                            {!equivalentActivitiesLoading && !equivalentActivitiesError && equivalentActivities.length > 0 && (
                                <div className="ad-list">
                                    {equivalentActivities.map((conversion) => (
                                        <div key={conversion.activity_id} className="ad-equiv">
                                            <div className="ad-equiv-body">
                                                <div className="ad-equiv-name">
                                                    {conversion.activity_name || formatActivityName(conversion.activity_id)}
                                                </div>
                                                {conversion.answers && conversion.answers.length > 0 && (
                                                    <div className="ad-equiv-meta">{conversion.answers.join(' · ')}</div>
                                                )}
                                            </div>
                                            <span className="ad-equiv-time">
                                                {formatTime(conversion.minutes)}
                                            </span>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </section>
                    )}

                    <section className="ad-section">
                        <div className="ad-section-head ad-section-head--split">
                            <h3 className="ad-section-title">Logged by</h3>
                            {isOwnActivity && (
                                <button
                                    type="button"
                                    className="sc-btn sc-btn--danger sc-btn--sm"
                                    onClick={() => setShowDeleteConfirm(true)}
                                >
                                    Delete
                                </button>
                            )}
                        </div>
                        <button type="button" className="ad-user" onClick={openProfile}>
                            <span className="ad-user-avatar">
                                {userAvatar && (
                                    <Avatar
                                        svgContent={userAvatar}
                                        alt={`${activity.username || 'User'}'s avatar`}
                                        className="ad-avatar"
                                    />
                                )}
                            </span>
                            <span className="ad-user-info">
                                <span className="ad-user-name">{activity.username || 'Unknown'}</span>
                                {loggedAt && <span className="ad-user-meta">Logged {loggedAt}</span>}
                            </span>
                            <span className="ad-user-arrow" aria-hidden="true">›</span>
                        </button>
                    </section>

                    <section className="ad-section">
                        <h3 className="ad-section-title">Reactions</h3>
                        {reactionGroups.length > 0 ? (
                            <div className="ad-list">
                                {reactionGroups.map((group) => (
                                    <div key={group.emoji} className="ad-reaction">
                                        <span className="ad-reaction-emoji" aria-hidden="true">{group.emoji}</span>
                                        <span className="ad-reaction-users">
                                            {group.users.map((user) => user.username).filter(Boolean).join(', ')}
                                        </span>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <p className="ad-note">No reactions yet. Be the first!</p>
                        )}
                        <div className="ad-reaction-picker">
                            {emojiChoices.map((emoji) => {
                                const reacted = activityReactions.some(
                                    (reaction) => reaction.emoji === emoji && reaction.user_id === currentUserId
                                );
                                return (
                                    <button
                                        key={emoji}
                                        type="button"
                                        className={`ad-reaction-btn${reacted ? ' is-active' : ''}`}
                                        aria-pressed={reacted}
                                        aria-label={reacted ? `Remove ${emoji} reaction` : `React with ${emoji}`}
                                        onClick={() => handleToggleReaction(emoji)}
                                    >
                                        {emoji}
                                    </button>
                                );
                            })}
                        </div>
                    </section>
                </div>
            </div>
        </div>
    );
};

export default ActivityDetailsPage;
